import { MUICheckBox, MUIDateTimePicker, MUISelect } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import dayjs, { Dayjs } from "dayjs"
import { StatusCodes } from "http-status-codes"
import { MouseEvent, useCallback, useEffect, useState } from "react"
import { Controller, ControllerRenderProps, useForm, useWatch } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useApplicationPermissions } from "../../../../../hooks/useApplicationPermissions"
import { useApplications } from "../../../../../hooks/useApplications"
import { digitalManagerApi } from "../../../../../services/api"
import { FieldRow, FormPage } from "../../../../components/FormPage"
import { useApplicationSelectRenderer } from "../../../../components/Helpers"
import { ApplicationPermissionGet } from "../../../../models/ApplicationPermission"
import {
  ApplicationPermissionUserGet,
  ApplicationPermissionUserPost,
} from "../../../../models/ApplicationPermissionUser"
import { AccessLevel, DimaFeatureShortNameEnum } from "../../../../models/Authorization"
import { Guid } from "../../../../models/CustomType"
import { ControllerRendererProps } from "../../../../types/reactHookForm"
import { getNowDateTime } from "../../../../utils/getFormattedDate"

const validationSchema = z.object({
  applicationId: z.string().min(1, "Application is required"),
  permissionId: z.string().min(1, "Permission is required"),
  validityStartDate: z
    .string()
    .min(1, "Validity Start Date is required")
    .refine((val) => !Number.isNaN(Date.parse(val)), "Invalid date format"),
  validityEndDate: z
    .string()
    .min(1, "Validity End Date is required")
    .refine((val) => !Number.isNaN(Date.parse(val)), "Invalid date format"),
  isDisabled: z.boolean({ error: "Is Disabled is required" }),
})

const MAX_DATE = dayjs("9999-12-31T23:59:59")

export function UserApplicationsPermissionCreate() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const returnURL = `/users/${userId}#apps-permissions`
  const { applicationsList, isLoadingApplications } = useApplications(
    DimaFeatureShortNameEnum.RM,
    AccessLevel.Write
  )
  const [userPermissionsIds, setUserPermissionsIds] = useState<Guid[]>([])

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<
    { applicationId: string; permissionId: string } & ApplicationPermissionUserPost
  >({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      applicationId: "",
      permissionId: "",
      validityStartDate: new Date(getNowDateTime()).toISOString(),
      validityEndDate: "",
      isDisabled: false,
    })
  }, [reset])

  const applicationId = useWatch({
    control,
    name: "applicationId",
  })

  const onSubmit = useCallback(
    (
      values: {
        applicationId: string
        permissionId: string
      } & ApplicationPermissionUserPost
    ) => digitalManagerApi
        .post(
          `/users/${userId}/applications/${values.applicationId}/permissions/${values.permissionId}`,
          {
            validityStartDate: values.validityStartDate,
            validityEndDate: values.validityEndDate,
            isDisabled: values.isDisabled,
          }
        )
        .then((res) => {
          if (res.status === StatusCodes.CREATED) {
            toast.success("Permission added to user")
            navigate(returnURL)
          }
        })
        .catch(() => {
          toast.error("Cannot add permission to user")
        }),
    [userId, navigate, returnURL]
  )

  useEffect(() => {
    if (applicationId) {
      digitalManagerApi
        .get(`/users/${userId}/permissions`, {
          params: { applicationId },
        })
        .then((res) => {
          if (res.status === StatusCodes.OK && res.data?.length) {
            setUserPermissionsIds(
              res.data.map(
                (userPermission: ApplicationPermissionUserGet) =>
                  userPermission.permission.id
              )
            )
          }
        })
    }
  }, [applicationId, userId])

  const { applicationPermissions, isLoadingApplicationPermissions } =
    useApplicationPermissions(applicationId)

  const handleAutocompleteGetOptionDisabled = useCallback(
    (option: ApplicationPermissionGet) => userPermissionsIds.includes(option.id),
    [userPermissionsIds]
  )

  const handleAutocompleteGetOptionLabel = useCallback(
    (option: ApplicationPermissionGet) => option.name,
    []
  )

  const handleApplicationChange = useCallback(
    (value: string) => {
      setValue("applicationId", value, { shouldValidate: true })
      setValue("permissionId", "", { shouldValidate: true })
    },
    [setValue]
  )

  const submitForm = handleSubmit(onSubmit)

  const handleFieldAndApplicationChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; permissionId: string } & ApplicationPermissionUserPost,
          "applicationId"
        >
      ) =>
      (value: string) => {
        field.onChange(value)
        handleApplicationChange(value)
      },
    [handleApplicationChange]
  )

  const handleSelectApplicationIdChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; permissionId: string } & ApplicationPermissionUserPost,
          "applicationId"
        >
      ) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : first?.id ?? ""
        handleFieldAndApplicationChange(field)(value)
      },
    [handleFieldAndApplicationChange]
  )

  const renderSelectApplicationId = useApplicationSelectRenderer({
    applicationsList,
    errors,
    isLoadingApplications,
    handleApplicationSelectChange: handleSelectApplicationIdChange,
  })

  const handleAutocompleteChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; permissionId: string } & ApplicationPermissionUserPost,
          "permissionId"
        >
      ) =>
      (next: Array<string | { id: string }>) => {
        const firstItem = next?.[0]
        const selectedId = typeof firstItem === "string" ? firstItem : firstItem?.id
        field.onChange(selectedId ?? "")
      },
    []
  )

  const renderAutocompletePermissionId = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      { applicationId: string; permissionId: string } & ApplicationPermissionUserPost,
      "permissionId"
    >) => (
      <MUISelect
        id="permissionId"
        name="permissionId"
        placeholder="Select permission"
        size={32}
        dropdownLOV={(applicationPermissions ?? []).map((permission) => ({
          id: permission.id,
          name: handleAutocompleteGetOptionLabel(permission) ?? "",
          isDisabled: handleAutocompleteGetOptionDisabled(permission),
        }))}
        enableSearch
        clearable={false}
        isDisabled={isLoadingApplicationPermissions}
        selectedItem={field.value ? [field.value] : []}
        onChange={handleAutocompleteChange(field)}
        errors={
          typeof errors.permissionId?.message === "string"
            ? { permissionId: { message: errors.permissionId.message } }
            : undefined
        }
      />
    ),
    [
      applicationPermissions,
      isLoadingApplicationPermissions,
      handleAutocompleteGetOptionLabel,
      handleAutocompleteGetOptionDisabled,
      handleAutocompleteChange,
      errors.permissionId?.message,
    ]
  )

  const handleDateChangeValidityStartDate = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; permissionId: string } & ApplicationPermissionUserPost,
          "validityStartDate"
        >
      ) =>
      (date: Dayjs | null) => {
        field.onChange(date ? date.toISOString() : "")
        const validityEndDate = getValues("validityEndDate")

        if (date?.isAfter(dayjs(validityEndDate))) {
          setValue("validityEndDate", "")
        }
      },
    [getValues, setValue]
  )

  const handleValidityStartDatePickerChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; permissionId: string } & ApplicationPermissionUserPost,
          "validityStartDate"
        >
      ) =>
      (value: Dayjs | null) =>
        handleDateChangeValidityStartDate(field)(value),
    [handleDateChangeValidityStartDate]
  )

  const renderDateTimePickerValidityStartDate = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      { applicationId: string; permissionId: string } & ApplicationPermissionUserPost,
      "validityStartDate"
    >) => (
      <MUIDateTimePicker
        id="validityStartDate"
        name="validityStartDate"
        size={32}
        value={field.value ? dayjs(field.value) : null}
        onChange={handleValidityStartDatePickerChange(field)}
        isDisabled={false}
        errors={
          errors.validityStartDate?.message
            ? { validityStartDate: { message: String(errors.validityStartDate.message) } }
            : undefined
        }
      />
    ),
    [errors.validityStartDate, handleValidityStartDatePickerChange]
  )

  const handleDateChangeValidityEndDate = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; permissionId: string } & ApplicationPermissionUserPost,
          "validityEndDate"
        >
      ) =>
      (date: Dayjs | null) => {
        field.onChange(date ? date.toISOString() : "")
      },
    []
  )

  const handleValidityEndDatePickerChange = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; permissionId: string } & ApplicationPermissionUserPost,
          "validityEndDate"
        >
      ) =>
      (date: Dayjs | null) => {
        const min = getValues("validityStartDate")
        const minDayjs = min ? dayjs(min) : null

        if (minDayjs && date?.isBefore(minDayjs)) {
          setValue("validityEndDate", "")
          return
        }

        if (date?.isAfter(MAX_DATE)) {
          setValue("validityEndDate", MAX_DATE.toISOString())
          return
        }

        handleDateChangeValidityEndDate(field)(date)
      },
    [getValues, handleDateChangeValidityEndDate, setValue]
  )

  const renderDateTimePickerValidityEndDate = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      { applicationId: string; permissionId: string } & ApplicationPermissionUserPost,
      "validityEndDate"
    >) => (
      <MUIDateTimePicker
        id="validityEndDate"
        name="validityEndDate"
        size={40}
        value={field.value ? dayjs(field.value) : null}
        isDisabled={false}
        onChange={handleValidityEndDatePickerChange(field)}
        errors={
          errors.validityEndDate
            ? { validityEndDate: { message: String(errors.validityEndDate.message) } }
            : undefined
        }
      />
    ),
    [errors.validityEndDate, handleValidityEndDatePickerChange]
  )

  const handleCheckboxToggle = useCallback(
    (
        field: ControllerRenderProps<
          { applicationId: string; permissionId: string } & ApplicationPermissionUserPost,
          "isDisabled"
        >
      ) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderCheckboxIsDisabled = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      { applicationId: string; permissionId: string } & ApplicationPermissionUserPost,
      "isDisabled"
    >) => (
      <MUICheckBox
        id="isDisabled"
        checked={!!field.value}
        handleToggle={handleCheckboxToggle(field)}
      />
    ),
    [handleCheckboxToggle]
  )

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={submitForm}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Application">
        <Controller
          name="applicationId"
          control={control}
          render={renderSelectApplicationId}
        />
      </FieldRow>
      <FieldRow label="Permission">
        <Controller
          name="permissionId"
          control={control}
          render={renderAutocompletePermissionId}
        />
      </FieldRow>
      <FieldRow label="Validity Start Date">
        <Controller
          name="validityStartDate"
          control={control}
          render={renderDateTimePickerValidityStartDate}
        />
      </FieldRow>
      <FieldRow label="Validity End Date">
        <Controller
          name="validityEndDate"
          control={control}
          render={renderDateTimePickerValidityEndDate}
        />
      </FieldRow>
      <FieldRow label="Is Disabled">
        <Controller
          name="isDisabled"
          control={control}
          render={renderCheckboxIsDisabled}
        />
      </FieldRow>
    </FormPage>
  )
}
